/**
 * **The eight factual claims a survey agent makes about the call itself** — unit P11-10.
 *
 * WHY THIS FILE EXISTS. `questionnaire.ts` validates the *script*: every approved line the
 * agent may speak. What it cannot do is check whether those lines are **true**. Eight of them
 * are not questions at all — they are assertions about who is calling, on whose behalf, how
 * the number was obtained, what is recorded and how long it will take. Read them in
 * surveybot's own Greek instrument and the point is unmissable:
 *
 *   opening.intro              "Είμαι η Μαρία … δεν πουλάμε τίποτα."
 *   objections.who_runs        "Είναι μια ανεξάρτητη έρευνα γνώμης. Ούτε ζητάμε ούτε
 *                               καταγράφουμε ονόματα."
 *   objections.where_phone     "Ο αριθμός βγήκε τυχαία, έχω μόνο έναν αριθμό, όχι όνομα."
 *   objections.how_long        "Λίγα λεπτάκια όλα κι όλα."
 *   objections.are_you_robot   "Έχετε δίκιο, είμαι ψηφιακή βοηθός."
 *
 * A question list cannot supply any of that, and **WeTalk must not invent it**: "we are not
 * selling anything" said by an agent that is selling, or "your number came up at random" said
 * about a purchased list, is a false statement made by a machine on a customer's behalf, in a
 * regulated setting, at scale. So the eight are **required fields**, collected from the
 * customer, and the extraction drafts the script around them for the customer to approve.
 *
 * WHAT THIS FILE DOES AND DELIBERATELY DOES NOT DO.
 *
 *  · It **validates** the eight, including the two that are not free text: a claim of "your
 *    number came up at random" is an enumerated `number_source`, not a sentence, because an
 *    enumeration can be checked against the panel and a sentence cannot.
 *  · It **reconciles** the one claim that is mechanically checkable — the interviewer's name
 *    must be the name the questionnaire speaks — and refuses the pair when they disagree.
 *  · It **maps every claim to the spoken lines that assert it**, so the approval step shows a
 *    customer exactly which sentences they are standing behind rather than a checkbox.
 *  · It does **not** try to read the other seven out of the approved text. The script is in
 *    twenty-six languages; a regex over Greek prose that decided whether a sentence promises
 *    anonymity would be a guess with legal weight. Naming the evidence and requiring a human
 *    to approve it is the honest mechanism.
 */
import { z } from "zod";
/**
 * The eight, by the field code the customer fills in.
 *
 * Three of them — `interviewer_name`, `survey_subject`, `survey_commissioner` — are already on
 * the survey template's field set. The other five were identified by P7 and left unbuilt; this
 * unit completes the set.
 */
export const SURVEY_CLAIM = [
    "interviewer_name",
    "survey_subject",
    "survey_commissioner",
    "commercial_intent",
    "personal_name_recorded",
    "number_source",
    "expected_duration_minute",
    "machine_disclosure",
];
/**
 * Whether anything is being sold, asked for or campaigned for.
 *
 * `none` is the only value under which the agent may say "we are not selling anything". The
 * other three exist because they are the three things a cold call is most often accused of
 * being, and each has a different legal footing in the EU.
 */
export const COMMERCIAL_INTENT = ["none", "selling", "fundraising", "campaigning"];
/**
 * Where the respondent's number came from.
 *
 * surveybot says "Ο αριθμός βγήκε τυχαία" — the number came up at random. That sentence is
 * only true for `random_digit_dialling`. For a bought panel or the customer's own contacts the
 * honest answer is different, and GDPR Article 14 wants it said.
 */
export const NUMBER_SOURCE = [
    "random_digit_dialling",
    "purchased_panel",
    "own_contact_list",
    "public_register",
];
/** How long a survey may claim to take before the claim stops being credible. */
export const MAX_CLAIMED_DURATION_MINUTE = 30;
const NonEmpty = z.string().trim().min(1);
/**
 * The brief, parsed.
 *
 * Field names are camelCase here because this is a TypeScript value, not a document a survey
 * agent consumes — `questionnaire.ts`'s snake_case is deliberate for the opposite reason.
 */
export const SurveyBriefSchema = z
    .object({
    /** The name the respondent hears. It must be the name the questionnaire speaks. */
    interviewerName: NonEmpty.max(60),
    /** What the survey is about, in the words a respondent should hear. */
    surveySubject: NonEmpty.max(200),
    /** Who is running it. "An independent opinion survey" is an answer; "" is not. */
    surveyCommissioner: NonEmpty.max(240),
    commercialIntent: z.enum(COMMERCIAL_INTENT),
    /** True when a respondent's name is asked for or written down at any point. */
    personalNameRecorded: z.boolean(),
    numberSource: z.enum(NUMBER_SOURCE),
    expectedDurationMinute: z
        .number()
        .int()
        .min(1)
        .max(MAX_CLAIMED_DURATION_MINUTE),
    /**
     * Whether the agent admits it is a machine when asked.
     *
     * Required to be `true`. It is the one claim with no legitimate second value: an automated
     * caller that denies being automated when asked directly is deceiving the respondent, and
     * no configuration should be able to arrange that.
     */
    machineDisclosure: z.literal(true),
})
    .strict();
function problemOf(problem, file, key) {
    const location = key === undefined ? { file } : { file, key };
    return { ok: false, problem, location };
}
/** The label the wizard shows for each claim, so a refusal can name the field a person saw. */
export const SURVEY_CLAIM_LABEL = {
    interviewer_name: "The agent's name",
    survey_subject: "What the survey is about",
    survey_commissioner: "Who is calling, as people should hear it",
    commercial_intent: "Is anything being sold, asked for or campaigned for?",
    personal_name_recorded: "Is the respondent's name asked for or written down?",
    number_source: "Where the numbers you are calling came from",
    expected_duration_minute: "How many minutes it takes, as the caller is told",
    machine_disclosure: "The agent says it is a digital assistant when asked",
};
/**
 * Which approved lines each claim underwrites.
 *
 * This is the evidence the approval step renders. `opening.intro` carries three claims at
 * once, which is why an edit to one sentence has to re-open three of them.
 */
export const SURVEY_CLAIM_EVIDENCE = {
    interviewer_name: ["interviewer", "opening.intro", "objections.are_you_local.answer"],
    survey_subject: ["title", "opening.intro", "objections.what_is_this.answer"],
    survey_commissioner: ["objections.who_runs.answer", "objections.unknown.answer"],
    commercial_intent: ["opening.intro", "objections.what_is_this.answer"],
    personal_name_recorded: ["objections.who_runs.answer", "objections.where_phone.answer"],
    number_source: ["objections.where_phone.answer"],
    expected_duration_minute: ["objections.how_long.answer"],
    machine_disclosure: ["objections.are_you_robot.answer"],
};
/**
 * Reads the eight out of the wizard's `fieldValue` map.
 *
 * The map is `code -> what the customer typed`, so booleans and numbers arrive as strings and
 * are parsed here. An absent field is a refusal naming the field, never a default: a survey
 * whose duration claim defaulted to "a few minutes" would be a machine inventing a promise.
 */
export function surveyBriefOf(fieldValue, file = "(the survey brief)") {
    for (const claim of SURVEY_CLAIM) {
        const given = fieldValue[claim];
        if (given === undefined || given.trim() === "") {
            return problemOf(`"${SURVEY_CLAIM_LABEL[claim]}" has not been answered. It is one of eight statements ` +
                "the agent makes about the call itself, and wetalk will not invent one: a question " +
                "list cannot supply it, and a wrong answer here is a false claim made by a machine.", file, claim);
        }
    }
    const duration = Number(fieldValue["expected_duration_minute"]);
    const parsed = SurveyBriefSchema.safeParse({
        interviewerName: fieldValue["interviewer_name"],
        surveySubject: fieldValue["survey_subject"],
        surveyCommissioner: fieldValue["survey_commissioner"],
        commercialIntent: fieldValue["commercial_intent"],
        personalNameRecorded: readBoolean(fieldValue["personal_name_recorded"]),
        numberSource: fieldValue["number_source"],
        expectedDurationMinute: Number.isFinite(duration) ? duration : fieldValue["expected_duration_minute"],
        machineDisclosure: readBoolean(fieldValue["machine_disclosure"]),
    });
    if (!parsed.success) {
        const issue = parsed.error.issues[0];
        const path = issue === undefined ? "" : issue.path.join(".");
        const claim = CLAIM_BY_FIELD[path];
        const detail = parsed.error.issues
            .map((one) => `${one.path.join(".") || "(root)"}: ${one.message}`)
            .join("; ");
        if (path === "machineDisclosure") {
            return problemOf("The agent must say it is a digital assistant when a respondent asks. There is no " +
                "second value for this one: an automated caller that denies being automated when " +
                "asked directly is deceiving the person it called.", file, "machine_disclosure");
        }
        return problemOf(detail, file, claim ?? (path === "" ? undefined : path));
    }
    return { ok: true, brief: parsed.data };
}
const CLAIM_BY_FIELD = {
    interviewerName: "interviewer_name",
    surveySubject: "survey_subject",
    surveyCommissioner: "survey_commissioner",
    commercialIntent: "commercial_intent",
    personalNameRecorded: "personal_name_recorded",
    numberSource: "number_source",
    expectedDurationMinute: "expected_duration_minute",
    machineDisclosure: "machine_disclosure",
};
/** `"true"`/`"yes"`/`"on"` are true; everything else is false. Absence was refused above. */
function readBoolean(value) {
    const raw = (value ?? "").trim().toLowerCase();
    return raw === "true" || raw === "yes" || raw === "on" || raw === "1";
}
/**
 * The one reconciliation that can be made mechanically: the interviewer's name.
 *
 * `survey.interviewer` is the name the script speaks; `brief.interviewerName` is the name the
 * customer says the agent is called. If they disagree, one of them is wrong, and which one is
 * not for this function to decide — so it refuses the pair and names both.
 *
 * The other seven are **not** checked against the prose, deliberately. The script runs in
 * twenty-six languages, and a regex over Greek or Hebrew that decided whether a sentence
 * promises anonymity would be a guess carrying legal weight. They are surfaced as evidence
 * for a human to approve instead, which is what {@link surveyClaimEvidenceOf} is for.
 */
export function reconcileSurveyBrief(brief, survey, file) {
    const spoken = survey.interviewer.trim();
    if (spoken.toLowerCase() !== brief.interviewerName.trim().toLowerCase()) {
        return problemOf(`The questionnaire introduces the interviewer as "${spoken}", and the brief says the ` +
            `caller hears "${brief.interviewerName}". One of the two is wrong and this unit will ` +
            "not choose: the name is spoken in the first sentence of every call.", file, "interviewer");
    }
    if (!survey.opening.intro.includes(spoken)) {
        return problemOf(`The opening line does not say the interviewer's name ("${spoken}"). A survey call that ` +
            "never introduces the caller is the kind a respondent hangs up on, and in several " +
            "jurisdictions it is also the kind that is not allowed.", file, "opening.intro");
    }
    return null;
}
/**
 * Every claim with the lines it underwrites, for the approval step.
 *
 * A key the questionnaire does not carry is skipped rather than rendered empty — `objections`
 * is validated to hold all eight types, so a missing key here means an older questionnaire,
 * and an empty quotation mark in an approval screen is worse than one fewer line.
 */
export function surveyClaimEvidenceOf(brief, survey) {
    const stated = {
        interviewer_name: brief.interviewerName,
        survey_subject: brief.surveySubject,
        survey_commissioner: brief.surveyCommissioner,
        commercial_intent: brief.commercialIntent,
        personal_name_recorded: brief.personalNameRecorded ? "yes" : "no",
        number_source: brief.numberSource,
        expected_duration_minute: String(brief.expectedDurationMinute),
        machine_disclosure: "yes",
    };
    return SURVEY_CLAIM.map((claim) => ({
        claim,
        label: SURVEY_CLAIM_LABEL[claim],
        stated: stated[claim],
        spoken: SURVEY_CLAIM_EVIDENCE[claim]
            .map((key) => spokenAt(survey, key))
            .filter((one) => one !== null),
    }));
}
/** Reads one dotted key out of a questionnaire. Only the keys the evidence map names. */
function spokenAt(survey, key) {
    if (key === "interviewer")
        return survey.interviewer;
    if (key === "title")
        return survey.title;
    if (key === "opening.intro")
        return survey.opening.intro;
    const objection = /^objections\.([a-z_]+)\.answer$/.exec(key);
    if (objection !== null) {
        const type = objection[1];
        if (type === undefined)
            return null;
        const found = survey.objections[type];
        if (found === undefined || found.answer.trim() === "")
            return null;
        return found.answer;
    }
    return null;
}
//# sourceMappingURL=brief.js.map